import React, { useEffect, useState } from 'react';
import { useI18n } from '@/i18n/I18nContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { decryptJSON, isCryptoAvailable } from '@/lib/crypto';
import { LockKeyhole, Eye, EyeOff, KeyRound } from 'lucide-react';

export const UnlockDialog = ({ open, payload, onUnlock, onClose }) => {
  const { t } = useI18n();
  const [pwd, setPwd] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setPwd('');
      setShow(false);
      setBusy(false);
      setError('');
    }
  }, [open]);

  const unlock = async () => {
    if (!payload) return;
    setError('');
    if (!isCryptoAvailable()) {
      setError(t('crypto_unavailable'));
      return;
    }
    if (!pwd) {
      setError(t('unlock_wrong'));
      return;
    }
    setBusy(true);
    try {
      const data = await decryptJSON(payload, pwd);
      const isEnvelope = data && data.kind === 'switchr-share' && data.doc;
      const doc = isEnvelope ? data.doc : data;
      const mode = isEnvelope && data.mode === 'view' ? 'view' : 'full';
      if (!doc || typeof doc !== 'object' || !Array.isArray(doc.pages)) {
        throw new Error('invalid');
      }
      onUnlock(doc, mode);
    } catch (e) {
      setError(t('unlock_wrong'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose && onClose()}>
      <DialogContent className="max-w-md" data-testid="unlock-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <LockKeyhole className="h-5 w-5 text-[#0B5FFF]" /> {t('unlock_title')}
          </DialogTitle>
          <DialogDescription>{t('unlock_desc')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">{t('unlock_password')}</Label>
            <div className="relative">
              <KeyRound className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                type={show ? 'text' : 'password'}
                value={pwd}
                onChange={(e) => setPwd(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && unlock()}
                className="h-9 pl-8 pr-9"
                placeholder={t('unlock_password')}
                data-testid="unlock-password-input"
                autoFocus
              />
              <button
                type="button"
                onClick={() => setShow((s) => !s)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                aria-label={show ? 'hide' : 'show'}
                data-testid="unlock-password-toggle"
              >
                {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          {error && (
            <p className="text-sm text-[#C2410C]" data-testid="unlock-error">
              {error}
            </p>
          )}

          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => onClose && onClose()} disabled={busy} data-testid="unlock-cancel-button">
              {t('cancel')}
            </Button>
            <Button onClick={unlock} disabled={busy} data-testid="unlock-button">
              <LockKeyhole className="mr-2 h-4 w-4" />
              {busy ? t('unlock_loading') : t('unlock_button')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
